import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Card, Button } from "react-bootstrap";
import { RootState } from "@reducers/rootReducer";
import { useCreatePromptOverrideMutation } from "@generated";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { handleAlert } from "@utils/helperFunctions";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import PromptOverrideForm from "../common/Form/Form";
import { INITIAL_FORM_STATE, PromptOverrideFormState } from "../common/types";
import { formatPromptOverrideData } from "../common/helpers";
import "../style.css";

const New = (): JSX.Element => {
  const navigate = useNavigate();
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const [createPromptOverride] = useCreatePromptOverrideMutation();

  const [formState, setFormState] =
    useState<PromptOverrideFormState>(INITIAL_FORM_STATE);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showFailAlert, setShowFailAlert] = useState(false);

  const handleSave = () => {
    if (!formState.promptType || !formState.content) return;

    setLoading(true);
    const data = formatPromptOverrideData(formState);

    createPromptOverride({ slug: accountSlug, body: { data } })
      .unwrap()
      .then((res) => {
        const id = res?.data?.id;
        if (id) {
          navigate(`/admin/prompt_overrides/${id}/edit`, {
            state: { refetch: true },
          });
        } else {
          setErrorMessage("Failed to create prompt override.");
          setShowFailAlert(true);
        }
      })
      .catch((error) => {
        console.error("API Error:", error);
        setErrorMessage(
          error?.data?.errors?.[0]?.detail || "An error occurred."
        );
        setShowFailAlert(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      {handleAlert(
        showFailAlert,
        errorMessage,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      <Card
        style={{
          padding: "16px",
          borderRadius: "4px",
          border: "1px solid var(--gray-3-stroke, #D7D7D7)",
          background: "var(--white, #FFF)",
          boxShadow: "0px 2px 3px 0px rgba(0, 0, 0, 0.15)",
        }}
      >
        <Card.Header
          style={{
            backgroundColor: "#fff",
            lineHeight: "40px",
            display: "block",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div
              className="accordianTitle"
              style={{ height: "40px", margin: "0", alignContent: "center" }}
            >
              <Button
                className="btnSeawhite"
                onClick={() => navigate("/admin/prompt_overrides")}
              >
                Cancel
              </Button>
              <span style={{ marginLeft: "12px" }}>Add a Prompt Override</span>
            </div>
            <div className="actionBtns">
              <Button
                className="squareGreenButton"
                onClick={handleSave}
                disabled={
                  loading || !formState.promptType || !formState.content
                }
              >
                {loading ? <SmallSpinner /> : "Save"}
              </Button>
            </div>
          </div>
          <hr />
        </Card.Header>
        <Card.Body style={{ padding: "0px 16px 16px 16px" }}>
          <PromptOverrideForm
            formState={formState}
            setFormState={setFormState}
            editing={false}
          />
        </Card.Body>
      </Card>
    </>
  );
};

export default New;
